import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  updateDoc,
  onSnapshot,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import app, {
  db,
  functions,
  getFunctionsBaseUrl,
  USE_FIREBASE_EMULATORS,
  EMULATOR_PORTS,
} from '../firebase';
import { resolveUserRoleFromLists } from './portalAccess';
import { parsePasswordSignupResult } from './parsePasswordSignupResult';

const auth = getAuth();

// Cache for allowlist, admins, consultants, and support
let cachedAllowlist = null;
let cachedAdmins = null;
let cachedConsultants = null;
let cachedSupport = null;
let allowlistListener = null;
let adminsListener = null;
let consultantsListener = null;
let supportListener = null;
let ensureDefaultAdminPromise = null;

const normalizeEmail = (email) => (email || '').toLowerCase().trim();

/** Case-insensitive membership check for email arrays. */
const listIncludesEmail = (list, email) => {
  const e = normalizeEmail(email);
  if (!e) return false;
  return (list || []).some((x) => normalizeEmail(x) === e);
};

const roleDocRef = (id) => doc(db, 'userManagement', id);

/** Prefer server snapshot so IndexedDB/offline cache cannot feed stale role lists. */
const getRoleDocFromServer = async (ref) => {
  try {
    return await getDocFromServer(ref);
  } catch {
    return getDoc(ref);
  }
};

/** All case variants of an email currently stored in a list (for arrayRemove). */
const caseVariantsOf = (list, email) => {
  const e = normalizeEmail(email);
  return (list || []).filter((x) => normalizeEmail(x) === e);
};

/**
 * Atomically remove every case-variant of email from a role doc, then optionally arrayUnion lowercase.
 * Never replaces the whole emails array (avoids stale last-write-wins wipes).
 */
const patchRoleEmails = async (ref, email, { add = false } = {}) => {
  const e = normalizeEmail(email);
  if (!e) return;

  const snap = await getRoleDocFromServer(ref);
  if (!snap.exists()) {
    if (add) {
      await setDoc(ref, { emails: [e], updatedAt: new Date().toISOString() });
    }
    return;
  }

  const variants = caseVariantsOf(snap.data().emails || [], e);
  if (variants.length > 0) {
    await updateDoc(ref, { emails: arrayRemove(...variants), updatedAt: new Date().toISOString() });
  }
  if (add) {
    await updateDoc(ref, { emails: arrayUnion(e), updatedAt: new Date().toISOString() });
  }
};

// Initialize real-time listeners for allowlist, admins, and consultants
export const initializeUserManagementListeners = () => {
  try {
    const allowlistRef = doc(db, 'userManagement', 'allowlist');
    allowlistListener = onSnapshot(allowlistRef, (snap) => {
      if (snap.exists()) {
        cachedAllowlist = snap.data().emails || [];
      } else {
        cachedAllowlist = [];
      }
    }, (error) => {
      console.error('Error listening to allowlist:', error);
      cachedAllowlist = null;
    });

    const adminsRef = doc(db, 'userManagement', 'admins');
    adminsListener = onSnapshot(adminsRef, (snap) => {
      if (snap.exists()) {
        cachedAdmins = snap.data().emails || [];
      } else {
        cachedAdmins = [];
      }
    }, (error) => {
      console.error('Error listening to admins:', error);
      cachedAdmins = null;
    });

    const consultantsRef = doc(db, 'userManagement', 'consultants');
    consultantsListener = onSnapshot(consultantsRef, (snap) => {
      if (snap.exists()) {
        cachedConsultants = snap.data().emails || [];
      } else {
        cachedConsultants = [];
      }
    }, (error) => {
      console.error('Error listening to consultants:', error);
      cachedConsultants = null;
    });

    const supportRef = doc(db, 'userManagement', 'support');
    supportListener = onSnapshot(supportRef, (snap) => {
      if (snap.exists()) {
        cachedSupport = snap.data().emails || [];
      } else {
        cachedSupport = [];
      }
    }, (error) => {
      console.error('Error listening to support:', error);
      cachedSupport = null;
    });

    console.log('Firebase user management listeners initialized');
  } catch (error) {
    console.error('Error initializing listeners:', error);
  }
};

// Cleanup listeners
export const cleanupUserManagementListeners = () => {
  if (allowlistListener) {
    allowlistListener();
    allowlistListener = null;
  }
  if (adminsListener) {
    adminsListener();
    adminsListener = null;
  }
  if (consultantsListener) {
    consultantsListener();
    consultantsListener = null;
  }
  if (supportListener) {
    supportListener();
    supportListener = null;
  }
  cachedAllowlist = null;
  cachedAdmins = null;
  cachedConsultants = null;
  cachedSupport = null;
};

/** Drop cached role lists so the next read uses Firestore (needed right after login). */
export const invalidateUserManagementCache = () => {
  cachedAllowlist = null;
  cachedAdmins = null;
  cachedConsultants = null;
  cachedSupport = null;
};

// Check user access with caching (case-insensitive)
export const checkUserAccess = async (userEmail) => {
  try {
    if (!userEmail) return false;

    // Use cached allowlist if available
    if (cachedAllowlist !== null) {
      return listIncludesEmail(cachedAllowlist, userEmail);
    }

    // Fallback to direct Firestore query if cache is not available
    const allowlistRef = doc(db, 'userManagement', 'allowlist');
    const allowlistDoc = await getDoc(allowlistRef);

    if (allowlistDoc.exists()) {
      return listIncludesEmail(allowlistDoc.data().emails || [], userEmail);
    }

    // Fallback: check if user has custom claims
    const currentUser = auth.currentUser;
    if (currentUser) {
      const idTokenResult = await currentUser.getIdTokenResult();
      return !!idTokenResult.claims.allowed;
    }

    return false;
  } catch (error) {
    console.error('Error checking user access:', error);
    return false;
  }
};

// Check if user is admin with caching (case-insensitive)
export const isAdmin = async (userEmail) => {
  try {
    if (!userEmail) return false;

    // Use cached admin list if available
    if (cachedAdmins !== null) {
      return listIncludesEmail(cachedAdmins, userEmail);
    }

    // Fallback to direct Firestore query if cache is not available
    const adminsRef = doc(db, 'userManagement', 'admins');
    const adminsDoc = await getDoc(adminsRef);

    if (adminsDoc.exists()) {
      return listIncludesEmail(adminsDoc.data().emails || [], userEmail);
    }

    // Fallback: check custom claims
    const currentUser = auth.currentUser;
    if (currentUser) {
      const idTokenResult = await currentUser.getIdTokenResult();
      return idTokenResult.claims.admin === true;
    }

    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Get all allowed users from Firebase
export const getAllowedUsers = async () => {
  try {
    const allowlistRef = doc(db, 'userManagement', 'allowlist');
    const allowlistDoc = await getDoc(allowlistRef);
    
    if (allowlistDoc.exists()) {
      return allowlistDoc.data().emails || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting allowed users:', error);
    return [];
  }
};

// Get all support users from Firebase
export const getSupportUsers = async () => {
  try {
    if (!db) return [];
    const supportRef = doc(db, 'userManagement', 'support');
    const supportDoc = await getDoc(supportRef);
    if (supportDoc.exists()) {
      return supportDoc.data().emails || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting support users:', error);
    return [];
  }
};

/**
 * Get all users who can be assigned to quiz sessions.
 * Merges allowlist + admins + consultants + support so admins can assign to any platform user,
 * even if they were added to admins/consultants/support directly without being in allowlist.
 */
export const getAssignableUsers = async () => {
  try {
    const [allowlist, admins, consultants, support] = await Promise.all([
      getAllowedUsers(),
      getAdminUsers(),
      getConsultantUsers(),
      getSupportUsers(),
    ]);
    const seen = new Set();
    const result = [];
    for (const e of [...admins, ...consultants, ...support, ...allowlist]) {
      const normalized = (e || '').toLowerCase().trim();
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        result.push(normalized);
      }
    }
    return result.sort((a, b) => a.localeCompare(b));
  } catch (error) {
    console.error('Error getting assignable users:', error);
    return [];
  }
};

// Get all admin users from Firebase
export const getAdminUsers = async () => {
  try {
    const adminsRef = doc(db, 'userManagement', 'admins');
    const adminsDoc = await getDoc(adminsRef);
    if (adminsDoc.exists()) {
      return adminsDoc.data().emails || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting admin users:', error);
    return [];
  }
};

// Get all consultant users from Firebase
export const getConsultantUsers = async () => {
  try {
    const consultantsRef = doc(db, 'userManagement', 'consultants');
    const consultantsDoc = await getDoc(consultantsRef);
    if (consultantsDoc.exists()) {
      return consultantsDoc.data().emails || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting consultant users:', error);
    return [];
  }
};

/**
 * Create an email/password Auth user without switching the signed-in admin session.
 * Initial password is the email itself. Does not replace Google Sign-In.
 */
export const createPasswordAccountViaRest = async (email, password) => {
  const apiKey = app?.options?.apiKey;
  if (!apiKey) throw new Error('Firebase is not configured.');
  const base = USE_FIREBASE_EMULATORS
    ? `http://127.0.0.1:${EMULATOR_PORTS.auth}/identitytoolkit.googleapis.com/v1`
    : 'https://identitytoolkit.googleapis.com/v1';
  const res = await fetch(`${base}/accounts:signUp?key=${encodeURIComponent(apiKey)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, returnSecureToken: true }),
  });
  const json = await res.json().catch(() => ({}));
  return parsePasswordSignupResult(res.ok, json, email);
};

/**
 * Admin-only: create/enable Firebase Auth email+password for an Admin-added email.
 * Initial password is the email itself so Consultant Portal username and password
 * both use the Admin-added email ID. Existing password accounts are left unchanged.
 * Google Sign-In is not modified or replaced.
 */
export const provisionPortalAuthUser = async (email) => {
  const e = normalizeEmail(email);
  if (!e) return { skipped: true };
  if (!auth?.currentUser) {
    throw new Error('Must be signed in as admin to provision portal sign-in.');
  }

  const payload = { email: e };
  const tryCallable = async () => {
    if (!functions) throw new Error('Functions unavailable');
    const call = httpsCallable(functions, 'provisionPortalAuthUser');
    const res = await call(payload);
    return res?.data || { ok: true };
  };
  const tryHttp = async () => {
    const baseUrl = getFunctionsBaseUrl();
    const token = await auth.currentUser.getIdToken();
    if (!baseUrl || !token) throw new Error('Functions HTTP unavailable');
    const r = await fetch(`${baseUrl}/provisionPortalAuthUserHttp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ data: payload }),
    });
    if (!r.ok) {
      const errBody = await r.json().catch(() => ({}));
      throw new Error(errBody?.error?.message || r.statusText);
    }
    const json = await r.json();
    return json?.result || { ok: true };
  };

  try {
    return await tryCallable();
  } catch {
    try {
      return await tryHttp();
    } catch {
      const rest = await createPasswordAccountViaRest(e, e);
      if (rest.exists) {
        return {
          ...rest,
          warning:
            'This email already has a sign-in account. They can use Sign in with Google. Email/password is available after portal Auth provisioning succeeds.',
        };
      }
      return rest;
    }
  }
};

/**
 * Get user role from Firestore (single source of truth).
 * Priority: admin > support > consultant
 * Uses case-insensitive comparison so Firestore emails with different casing still match.
 */
export const getUserRole = async (userEmail) => {
  try {
    if (!userEmail) return null;
    const email = (userEmail || '').toLowerCase().trim();
    if (!email) return null;

    const [admins, consultants, support, allowlist] = await Promise.all([
      cachedAdmins !== null ? Promise.resolve(cachedAdmins) : getAdminUsers(),
      cachedConsultants !== null ? Promise.resolve(cachedConsultants) : getConsultantUsers(),
      cachedSupport !== null ? Promise.resolve(cachedSupport) : getSupportUsers(),
      cachedAllowlist !== null ? Promise.resolve(cachedAllowlist) : getAllowedUsers(),
    ]);

    return resolveUserRoleFromLists(email, { admins, consultants, support, allowlist });
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

/**
 * Get all users with their roles from Firestore.
 */
export const getUsersWithRoles = async () => {
  try {
    const [allowlist, admins, consultants, support] = await Promise.all([
      getAllowedUsers(),
      getAdminUsers(),
      getConsultantUsers(),
      getSupportUsers(),
    ]);

    const adminSet = new Set(admins.map((e) => e.toLowerCase()));
    const consultantSet = new Set(consultants.map((e) => e.toLowerCase()));
    const supportSet = new Set(support.map((e) => e.toLowerCase()));
    const allowlistSet = new Set(allowlist.map((e) => e.toLowerCase()));
    const allEmails = new Set([
      ...allowlist,
      ...admins,
      ...consultants,
      ...support,
    ].map((e) => (e || '').toLowerCase().trim()).filter(Boolean));

    return [...allEmails].map((e) => {
      let role = 'consultant';
      if (adminSet.has(e)) role = 'admin';
      else if (supportSet.has(e)) role = 'support';
      else if (consultantSet.has(e) || allowlistSet.has(e)) role = 'consultant';
      return {
        id: e,
        email: e,
        role,
        displayName: e.split('@')[0],
        createdAt: new Date().toISOString(),
      };
    });
  } catch (error) {
    console.error('Error getting users with roles:', error);
    return [];
  }
};

/**
 * Update a user's role in Firestore using arrayUnion/arrayRemove only.
 * Never replaces the whole emails[] field — that pattern was wiping consultants
 * when a stale offline/cached client synced minutes later.
 */
export const updateUserRoleInFirestore = async (email, newRole) => {
  if (!db) return;
  const e = normalizeEmail(email);
  if (!e) throw new Error('Email is required');

  const allowlistRef = roleDocRef('allowlist');
  const adminsRef = roleDocRef('admins');
  const consultantsRef = roleDocRef('consultants');
  const supportRef = roleDocRef('support');

  // Ensure docs exist (create empty if missing — does not wipe existing)
  const snaps = await Promise.all([
    getRoleDocFromServer(allowlistRef),
    getRoleDocFromServer(adminsRef),
    getRoleDocFromServer(consultantsRef),
    getRoleDocFromServer(supportRef),
  ]);
  const ensures = [];
  if (!snaps[0].exists()) ensures.push(setDoc(allowlistRef, { emails: [], createdAt: new Date().toISOString() }));
  if (!snaps[1].exists()) ensures.push(setDoc(adminsRef, { emails: [], createdAt: new Date().toISOString() }));
  if (!snaps[2].exists()) ensures.push(setDoc(consultantsRef, { emails: [], createdAt: new Date().toISOString() }));
  if (!snaps[3].exists()) ensures.push(setDoc(supportRef, { emails: [], createdAt: new Date().toISOString() }));
  if (ensures.length) await Promise.all(ensures);

  // Always keep on allowlist; remove from all role buckets; add to target role
  await patchRoleEmails(allowlistRef, e, { add: true });
  await patchRoleEmails(adminsRef, e, { add: newRole === 'admin' });
  await patchRoleEmails(consultantsRef, e, { add: newRole === 'consultant' });
  await patchRoleEmails(supportRef, e, { add: newRole === 'support' });
};

/**
 * Add a new user with role to Firestore.
 */
export const addUserWithRoleToFirestore = async (email, role) => {
  await updateUserRoleInFirestore(email, role);
};

/**
 * Remove a user from Firestore (allowlist, admins, consultants, support).
 * Uses arrayRemove only — never rewrites the full emails array from cache.
 */
export const removeUserFromFirestore = async (email) => {
  if (!db) return;
  const e = normalizeEmail(email);
  if (!e) return;

  await Promise.all([
    patchRoleEmails(roleDocRef('allowlist'), e, { add: false }),
    patchRoleEmails(roleDocRef('admins'), e, { add: false }),
    patchRoleEmails(roleDocRef('consultants'), e, { add: false }),
    patchRoleEmails(roleDocRef('support'), e, { add: false }),
  ]);
};

// Add user to allowlist in Firebase
export const addUserToAllowlist = async (email) => {
  try {
    const e = normalizeEmail(email);
    if (!e) return false;
    const allowlistRef = doc(db, 'userManagement', 'allowlist');
    const allowlistDoc = await getDoc(allowlistRef);

    if (!allowlistDoc.exists()) {
      await setDoc(allowlistRef, { emails: [e], createdAt: new Date().toISOString() });
      return true;
    }
    if (listIncludesEmail(allowlistDoc.data().emails || [], e)) {
      return false; // User already exists
    }
    await updateDoc(allowlistRef, { emails: arrayUnion(e) });
    return true;
  } catch (error) {
    console.error('Error adding user to allowlist:', error);
    throw error;
  }
};

// Remove user from allowlist in Firebase
export const removeUserFromAllowlist = async (email) => {
  try {
    const e = normalizeEmail(email);
    if (!e) return false;
    await patchRoleEmails(roleDocRef('allowlist'), e, { add: false });
    return true;
  } catch (error) {
    console.error('Error removing user from allowlist:', error);
    throw error;
  }
};

// Add admin user in Firebase
export const addAdminUser = async (email) => {
  try {
    const e = normalizeEmail(email);
    if (!e) return false;
    const adminsRef = doc(db, 'userManagement', 'admins');
    const adminsDoc = await getDoc(adminsRef);

    if (!adminsDoc.exists()) {
      await setDoc(adminsRef, { emails: [e], createdAt: new Date().toISOString() });
      return true;
    }
    if (listIncludesEmail(adminsDoc.data().emails || [], e)) {
      return false; // Admin already exists
    }
    await updateDoc(adminsRef, { emails: arrayUnion(e) });
    return true;
  } catch (error) {
    console.error('Error adding admin user:', error);
    throw error;
  }
};

// Remove admin user in Firebase
export const removeAdminUser = async (email) => {
  try {
    const e = normalizeEmail(email);
    if (!e) return false;
    await patchRoleEmails(roleDocRef('admins'), e, { add: false });
    return true;
  } catch (error) {
    console.error('Error removing admin user:', error);
    throw error;
  }
};

const DEFAULT_ADMIN_EMAIL = process.env.REACT_APP_DEFAULT_ADMIN_EMAIL || 'your-admin@example.com';

/**
 * Ensures the default admin exists in Firestore.
 * arrayUnion only — never touches consultants. Runs at most once per page load.
 */
export const ensureDefaultAdminInFirestore = async () => {
  if (!db) return;
  if (ensureDefaultAdminPromise) return ensureDefaultAdminPromise;

  ensureDefaultAdminPromise = (async () => {
    try {
      const email = normalizeEmail(DEFAULT_ADMIN_EMAIL);
      if (!email || email === 'your-admin@example.com') return;

      const allowlistRef = roleDocRef('allowlist');
      const adminsRef = roleDocRef('admins');
      const supportRef = roleDocRef('support');

      const [allowlistDoc, adminsDoc, supportDoc] = await Promise.all([
        getRoleDocFromServer(allowlistRef),
        getRoleDocFromServer(adminsRef),
        getRoleDocFromServer(supportRef),
      ]);

      if (!allowlistDoc.exists()) {
        await setDoc(allowlistRef, { emails: [email], createdAt: new Date().toISOString() });
      } else if (!listIncludesEmail(allowlistDoc.data().emails || [], email)) {
        await updateDoc(allowlistRef, { emails: arrayUnion(email) });
      }

      if (!adminsDoc.exists()) {
        await setDoc(adminsRef, { emails: [email], createdAt: new Date().toISOString() });
      } else if (!listIncludesEmail(adminsDoc.data().emails || [], email)) {
        await updateDoc(adminsRef, { emails: arrayUnion(email) });
      }

      if (!supportDoc.exists()) {
        await setDoc(supportRef, { emails: [], createdAt: new Date().toISOString() });
      }
    } catch (error) {
      console.error('Error ensuring default admin:', error);
      ensureDefaultAdminPromise = null; // allow retry on next sign-in
    }
  })();

  return ensureDefaultAdminPromise;
};

// Initialize Firebase user management collections (create-only; never wipe existing lists)
export const initializeUserManagement = async () => {
  if (!db) return;
  try {
    const defaultAdmin = normalizeEmail(
      process.env.REACT_APP_DEFAULT_ADMIN_EMAIL || 'your-admin@example.com'
    );
    const allowlistRef = doc(db, 'userManagement', 'allowlist');
    const adminsRef = doc(db, 'userManagement', 'admins');
    const consultantsRef = doc(db, 'userManagement', 'consultants');
    const supportRef = doc(db, 'userManagement', 'support');

    const [allowlistDoc, adminsDoc, consultantsDoc, supportDoc] = await Promise.all([
      getDoc(allowlistRef),
      getDoc(adminsRef),
      getDoc(consultantsRef),
      getDoc(supportRef),
    ]);

    if (!allowlistDoc.exists()) {
      await setDoc(allowlistRef, {
        emails: defaultAdmin ? [defaultAdmin] : [],
        createdAt: new Date().toISOString(),
      });
    }
    if (!adminsDoc.exists()) {
      await setDoc(adminsRef, {
        emails: defaultAdmin ? [defaultAdmin] : [],
        createdAt: new Date().toISOString(),
      });
    }
    if (!consultantsDoc.exists()) {
      await setDoc(consultantsRef, {
        emails: [],
        createdAt: new Date().toISOString(),
      });
    }
    if (!supportDoc.exists()) {
      await setDoc(supportRef, {
        emails: [],
        createdAt: new Date().toISOString(),
      });
    }

    console.log('Firebase user management initialized');
  } catch (error) {
    console.error('Error initializing user management:', error);
  }
};

// Monitor auth state changes
export const monitorAuthState = (onAuthChange) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const hasAccess = await checkUserAccess(user.email);
      const isAdminUser = await isAdmin(user.email);
      
      onAuthChange({
        user,
        hasAccess,
        isAdmin: isAdminUser
      });
    } else {
      onAuthChange({
        user: null,
        hasAccess: false,
        isAdmin: false
      });
    }
  });
};